/**
 * The house watermark, as every case-report template measured it (`Watermark - Mulish 30deg
 * V2`): 19.97 pt, 30°, opacity 0.169, pitch 58.081 × 16.849 mm. The generated truth record
 * that used to hold it per template retired on 26 Sep 2026 — the master (template.js) is
 * the record now, and its watermark is this one.
 */
const HOUSE_WATERMARK = {
  text: 'Osteo3d ®', size: 19.97, opacity: 0.169, angle: 30.0,
  stepX: 58.081, stepY: 16.849, color: [140, 150, 155],
}

/** Millimetres per PostScript point. */
const PT_TO_MM = 25.4 / 72

/**
 * geometry.js — the page chrome of an Osteo3d Case Report, in millimetres.
 *
 * Every number here was MEASURED off the shipped PDFs, not designed. Both
 * reports are image-only (PDF24 / cairo output of a hand-built document), so
 * there is no source file to read — the geometry was recovered by rasterising
 * each page at 300 dpi and finding the horizontal rules and ink bands:
 *
 *   ~/Documents/Temp/Orthognathic/DemoCase/Demo Orthognathic Case Report V1.1.pdf
 *   ~/Documents/Osteo3d - Leeky-Mac/Marketing/Presentation/
 *       Demo - Fibula Guide Case Report-1.pdf
 *
 * The two reports are NOT on the same grid — they were built years apart. The
 * orthognathic report sits on a 7.7 mm margin, the fibula report on 10.5 mm;
 * their header stacks differ by ~3.5 mm; the fibula report has no page numbers
 * and no "Images are not to scale" line. Rather than pick a winner and silently
 * move one of them, each template carries its own measured geometry and
 * reproduces its own deliverable exactly.
 *
 * ⚠️ If the team wants ONE house grid, that is a real decision with a visible
 * result — make it explicitly, don't let it happen as a side effect of a
 * refactor here.
 *
 * Rules are given as the TOP edge of the drawn line, matching how they were
 * measured. Text positions are BASELINES (what jsPDF's doc.text wants), derived
 * from the measured ink bands and then verified by re-measuring our own output —
 * see __tests__/caseReportGeometry.test.js.
 */

/** Measured ink bands from the reference PDFs, kept so the fit can be re-checked. */
/**
 * ⚠️ These are ink bands measured off a DELIVERED PDF — what one case actually
 * looked like once a human had filled it in. They are NOT what the template
 * says. The other record is `templateTruth.js`, and the two are kept apart
 * because they disagree in an informative way.
 *
 * The disagreement that forced the split: the orbital right info column
 * measured 132.40 mm here and was copied into `ORBITAL_GEOMETRY.info.rightColX`
 * as geometry. The template puts that column at 119.38, and a SECOND delivered
 * case puts its ink at 148.55 — 16 mm from the first. The team drags that block
 * per case, so no single delivered value was ever a house constant. See ADR
 * 0012.
 *
 * Every entry carries `kind` so a reader never has to infer which claim a
 * number is making.
 */
export const REFERENCE_BANDS = {
  orthognathic: {
    kind: 'delivered-pdf',
    source: 'Demo Orthognathic Case Report V1.1.pdf, page 3',
    pageMm: [210.0, 297.1],
    logoWordmark: [1.95, 11.77],
    logoTagline: [13.72, 18.37],
    titleLine1: [21.00, 25.82],
    titleLine2: [27.60, 32.43],
    ruleUnderTitle: 33.78,
    infoRow1: [37.76, 41.23],
    infoRow2: [44.03, 47.50],
    ruleUnderInfo: 49.78,
    captionTop: [51.22, 55.03],
    contentBox: [56.13, 263.99],   // 3 rows of 69.29 mm
    contentRowRules: [125.39, 194.73, 263.99],
    contentColRules: [7.70, 105.16, 202.69],
    captionBottom: [264.42, 269.25],
    ruleFooter: 269.41,
    footerProductLine: [272.38, 275.60],
    footerNotToScale: [277.29, 280.76],
    footerCopyright: [[282.97, 286.35], [287.37, 290.50]],
    footerContact: [291.52, 294.23],
  },
  orbital: {
    kind: 'delivered-pdf',
    source: 'Images for Design Confirmation - O3D-2026-06-18-0001_a.pdf (V1.3.3), pages 1-6',
    // Measured 2026-09-12 at 300 dpi, and at 600 dpi for the footer and legend.
    // The page-number band is EXCLUDED from footerProductLine because in this
    // reference it is a separate Helvetica vector stamp, not part of the raster.
    pageMm: [210.0, 297.0],
    logoInk: [2.52, 20.73],                // wordmark + tagline, one PNG
    titleLine: [22.73, 27.68],             // ONE line; p1 differs in text, not position
    ruleUnderTitle: 34.12,
    infoRow1: [35.93, 40.00],
    infoRow2: [43.31, 47.38],
    ruleUnderInfo: 50.12,
    // 🔴 Was `infoRightColX: 132.40`, and that scalar was copied into
    // ORBITAL_GEOMETRY as geometry. It never was. The template puts this column
    // at 119.38 mm; these are where the ink STARTS in each delivered case, and
    // they are 16 mm apart because the team drags the block when filling it in.
    // A scalar is what made a per-case artefact read as a house constant, so
    // the shape is deliberately plural now. See ADR 0012.
    infoRightColXPerCase: [132.40, 148.55],
    captionTop: [52.08, 55.38],            // page 2; per-page spread recorded in the docs
    contentBox: [56.22, 263.99],           // 3 rows of 69.26 mm
    contentRowRules: [125.48, 194.73, 263.99],
    contentColRules: [7.70, 105.16, 202.86],
    ruleFooter: 270.85,
    footerNotToScale: [271.85, 275.91],
    footerCopyright: [[276.80, 280.23], [281.15, 284.45]],
    footerContact: [285.35, 288.14],
    footerProductLine: [290.55, 293.98],   // isolated to x < 100 mm
    pageNumberStamp: {                     // NOT reproduced — see ADR 0009 §1
      band: [289.19, 292.23], x: [167.28, 186.63], font: 'Helvetica', size: 9.0,
    },
    legendSwatches: [
      { y: [242.66, 245.46], x: [124.33, 127.25], onPage: '#C27F90' },
      { y: [249.90, 252.70], x: [124.33, 127.12], onPage: '#887092' },
      { y: [256.89, 259.81], x: [124.58, 127.50], onPage: '#A4BA4E' },
    ],
    legendTextLeft: 129.78,
  },
  fibula: {
    kind: 'delivered-pdf',
    // ⚠️ V1.7. The live template is V1.9.2 and differs across the whole header,
    // so `FIBULA_GEOMETRY` is authored against `templateTruth.js` instead and
    // these bands are kept only as the record of what V1.7 looked like.
    source: 'Demo - Fibula Guide Case Report-1.pdf, page 9',
    pageMm: [210.0, 296.9],
    logoWordmark: [2.54, 12.28],
    logoTagline: [14.22, 18.88],
    titleLine1: [23.20, 28.02],
    titleLine2: [29.89, 34.63],
    ruleUnderTitle: 37.34,
    infoRow1: [40.89, 44.37],
    infoRow2: [46.74, 50.63],
    ruleUnderInfo: 53.17,
    contentBox: [55.0, 258.0],     // open frame — no drawn box; usable band
    captionBottom: [260.76, 266.43],
    ruleFooter: 268.72,
    footerProductLine: [271.68, 274.90],
    footerCopyright: [[282.18, 285.56], [286.58, 289.71]],
    footerContact: [290.64, 293.35],
  },
}

/**
 * Orthognathic Case Report V1.1 — the house geometry for the newer reports.
 *
 * Column rules measured at 7.70 / 105.16 / 202.69 → two equal 97.5 mm columns
 * on a 194.99 mm content width, i.e. margin 7.7 mm with the right edge landing
 * at 202.7 mm. Row rules 69.29 mm apart, three of them, box 56.13 → 264.0.
 */
export const ORTHOGNATHIC_GEOMETRY = {
  id: 'orthognathic',
  page: { w: 210, h: 297 },
  margin: 7.7,
  contentW: 195.0,

  logo: { top: 0.92, height: 18.11 },      // wordmark + tagline, the PNG carries both
  title: {
    line1Baseline: 24.73,
    line2Baseline: 31.34,
    size: 14.9,
  },
  ruleUnderTitle: { y: 33.78, width: 0.55 },

  info: {
    row1Baseline: 40.49,
    row2Baseline: 46.71,
    size: 10.5,
    // Left column starts at the margin; right column at the horizontal centre,
    // matching "Surgeon Name / Patient Name" and "Hospital Name / Case ID".
    rightColX: 105.16,
    lineH: 4.4,                            // leading for a wrapped value
  },
  ruleUnderInfo: { y: 49.78, width: 0.55 },

  // ⚠️ One size for the title and both captions, deliberately.
  // The fit against the reference came out title 14.91, top caption 14.41,
  // bottom caption 15.24 — three values straddling 14.9 on the SAME page. In a
  // hand-built document that is drift, not intent, so the generated report uses
  // one size. Worst-case deviation from the reference is ~0.5 mm of string
  // width. Say so if the team wants the original's variation reproduced.
  captionTop: { baseline: 55.07, size: 14.9 },
  content: { top: 56.13, bottom: 264.0 },
  captionBottom: { baseline: 268.87, size: 14.9 },

  ruleFooter: { y: 269.41, width: 0.55 },
  footer: {
    // The footer text is inset ~3 mm INSIDE the rules — measured, not assumed:
    // the rules span 7.70→202.61 but the footer runs 10.67→199.31.
    inset: 2.97,
    // MEASURED off the delivered report (p3) at 200 dpi on 2026-08-01 and
    // corrected: the product/page line is the LAST line on the page, not the
    // first line of the footer. The earlier values put it above "Images are not
    // to scale" and pushed everything else down a slot.
    //
    // Method, so this can be redone: threshold the footer crop, take each text
    // band's bottom edge, and subtract the per-element descender measured from
    // OUR OWN render of the same string (product 0.67, notToScale 0.79,
    // copyright 0.70/0.74, contact 0.52 mm). Self-calibrating — same font, same
    // renderer — rather than assuming a descender ratio.
    notToScaleBaseline: 273.10,
    copyrightBaselines: [279.00, 283.00],
    contactBaseline: 287.00,
    productBaseline: 293.90,
    productSize: 9.74,
    notToScaleSize: 11.22,
    copyrightSize: 9.85,
    // Fitted from band height, not width: our contact line's inter-field spacing
    // does not match the reference's, so a width fit would be measuring the
    // spaces. ±0.5pt here is invisible.
    contactSize: 8.83,
    showPageNumbers: true,
    showNotToScale: true,
  },


  // The case report's watermark is NOT the one in pdfChassis (that is the SSE
  // report's). Measured on the reference: ~4.4x more ink in a blank margin strip
  // and a ~9.8mm vertical repeat against the chassis's 26mm. Steps below are
  // MEASURED, not tuned by eye: on the largest blank region in the reference
  // (page 22, y 210-250mm) autocorrelation gives a 9.74mm vertical repeat and a
  // ~33.6mm horizontal one, and a single mark spans 30.8mm — which is 20pt
  // Mulish at 30 degrees, not the chassis's 13pt. At 0.14 opacity a blank margin
  // strip then carries 2.05 mean-grey units of ink against the reference's 2.06,
  // with the same 241/240 darkest pixel.
  //
  // Matching total ink alone is NOT enough: a first attempt hit the right
  // coverage with 13pt at 16x8mm and looked like grey mush, because it had the
  // density right and the tile size wrong.
  // MEASURED off this template's own baked watermark layer, not fitted.
  //
  // This was a hand-set constant — 33.6 x 9.74 mm at 0.14 — autocorrelated over
  // a blank strip of ONE delivered PDF. The horizontal period locked onto half
  // the true one, so the page carried 1.73x too many marks, at 0.14 against a
  // true 0.169. The team found it on a delivered page before we did. The
  // template says 58.08 x 16.85 at 0.169, 19.97 pt Mulish, 30 degrees.
  watermark: HOUSE_WATERMARK,

  frame: 'boxed',                          // the grid draws its box and dividers
  // Measured at 600 dpi: the reference's box edge and row dividers are a single
  // 0.085 mm black hairline, not the 0.2 mm I first assumed.
  gridRule: { width: 0.085 },
  viewBadge: { size: 17, inset: 2.0 },     // the big "A" / "LL" in a cell corner
}

/**
 * Free Fibula Flap Case Report — authored against the **V1.9.2 template**, read
 * with `apps/design/tools/xcf-geometry`. See `templateTruth.js`.
 *
 * ⚠️ This was previously fitted from a **V1.7 delivered PDF** and was wrong
 * across the whole header: the rules sat 3.3-3.5 mm low, the info block was
 * 10.5 pt instead of 12.0 with its right column 14 mm left of the template's,
 * the title was 14.9 pt instead of 14.4, and it recorded
 * `showNotToScale: false` when every one of the 12 current templates carries
 * that line. Nothing re-derived it, so none of that surfaced for a whole
 * template generation — which is the reason the extractor exists.
 *
 * The content box and margin are corrected separately, in their own commit and
 * ADR, because moving them moves every page on which anything is drawn.
 */
export const FIBULA_GEOMETRY = {
  id: 'fibula',
  page: { w: 210, h: 297 },
  // 🔴 ONE HOUSE GRID, adopted deliberately — see ADR 0013.
  //
  // Was margin 10.5 / contentW 189.0, fitted from the V1.7 delivered PDF. The
  // V1.9.2 template's grid layers state the same content frame as the orbital
  // and orthognathic templates: columns 7.75 / 105.20 / 202.73 mm as rule
  // centres, so 7.71 to 202.69 at the rules' left edges.
  //
  // geometry.js's own header asked for this to be decided explicitly rather
  // than happen as a refactor side effect. It moves every image on every fibula
  // page 2.8 mm left and widens the content by 5.99 mm, which is visible. The
  // measurement is what makes it a correction: 10.5 was a property of a
  // superseded document, and 8 of the 9 current templates that state a content
  // frame state this one, to within a pixel.
  margin: 7.7,
  contentW: 195.0,

  logo: { top: 1.69, height: 16.51 },
  title: {
    line1Baseline: 24.91,                  // 'Online Surgical Planning'
    line2Baseline: 31.43,                  // 'Case Report of Mandibular Reco…'
    size: 14.4,
  },
  ruleUnderTitle: { y: 33.86, width: 0.59 },

  info: {
    row1Baseline: 39.54,
    row2Baseline: 46.91,
    size: 12.0,
    // 119.38 in this template AND in the orbital one — a house value, and
    // 14 mm right of the 105.0 that was inherited from the content column rule.
    rightColX: 119.38,
    // The WRAP LEADING inside one info cell, not the gap between the two rows
    // (which is 7.37 mm and is encoded by the two baselines above). 1.28 em,
    // measured on 30 single-line labels across 11 templates at four sizes.
    lineH: 5.42,
  },
  ruleUnderInfo: { y: 49.86, width: 0.59 },

  // ⚠️ The captions are the SAME size as the title, not larger. The old comment
  // here said "the fibula report's captions really ARE larger than its title
  // (16.3 / 17.4 against 14.9) — visibly so on the page". That was another
  // artefact of the V1.7 fitting: measured across the template's 26 page
  // variants, every caption is 14.4 pt, and so is the title.
  //
  // Baselines are the modal value across those variants, which vary by hand:
  // the top caption sits at 55.13 on 9 of 19 pages within a 54.71-55.13 spread,
  // and the bottom at a median 268.24 within 268.07-268.75. One authored value
  // per slot, per ADR 0009 — the spread is hand-built drift, not design.
  // `fit` because this report has two captions that are whole sentences, and
  // the team shrinks them by hand. MEASURED on the delivered March 2026 case:
  // an ordinary caption is 14.4 pt (4.91 mm of ink) but the ~120-character
  // sentence on its pages 9 and 15 — "The reconstruction has been done with the
  // distal fibular segment positioned at the patient's anterior resection
  // margin of the mandible" — is set at roughly 8 pt and spans 188.1 mm of the
  // 195 mm column. At the authored size it would run into both margins.
  //
  // Same ruling as ADR 0009 for the orbital template: author ONE size so short
  // captions stay identical across pages, and shrink only when a caption would
  // overrun.
  //
  // `fitFloor` 0.55, not the default 0.70, and it is measured rather than
  // chosen: 2.79 mm of ink is about 8 pt, and 8 / 14.4 = 0.56. At a 0.70 floor
  // the shrink stops at 10.1 pt with the sentence still 44 mm wider than the
  // column — fitted according to the flag, overrunning on the page. The floor
  // is a readability judgement, and this is the team's own for this template.
  captionTop: { baseline: 55.13, size: 14.4, fit: true, fitFloor: 0.55 },
  content: { top: 56.22, bottom: 263.99 },
  captionBottom: { baseline: 268.24, size: 14.4 },

  ruleFooter: { y: 269.32, width: 0.59 },
  footer: {
    inset: 0,                              // measured flush with the margin here
    // MEASURED now, two ways that agree. The old comment said these were
    // "UNVERIFIED … probably shares the fault" of the orthognathic footer's
    // mis-slotted product line, and could not be corrected without a
    // measurement. The V1.9.2 template supplies one.
    //
    // Layer baselines (box top + 1.020 font sizes) cross-checked against the
    // ink bands of the template's own flattened render, which was necessary
    // because this footer has overlapping legacy layers — one holds
    // 'Phone: …' behind a leading newline, so its first line is empty and its
    // layer offset is a line above where it prints:
    //
    //   rendered band       content              baseline
    //   269.32 - 269.92     the rule itself      (0.59 thick)
    //   270.51 - 274.15     not to scale         273.30
    //   274.74 - 278.38     copyright line 1     277.60
    //   279.06 - 282.45     copyright line 2     281.67
    //   283.29 - 286.43     phone AND email      285.82
    //   289.39 - 292.69     product line         291.91
    notToScaleBaseline: 273.30,
    copyrightBaselines: [277.60, 281.67],
    contactBaseline: 285.82,
    productBaseline: 291.91,
    productSize: 9.6,
    notToScaleSize: 10.8,
    copyrightSize: 9.6,
    contactSize: 9.6,
    // 🔴 Was false, on the strength of "no template carries a page-number
    // layer". That was wrong, and it was wrong because the layers are authored
    // SWITCHED OFF — the team enables and edits one per page at export, so a
    // visibility-filtered scan found none in any of the 12.
    //
    // Six templates carry one, being the six standalone case reports. The
    // fibula's sits at x 177.29, 9.6 pt, baseline 291.91 — the SAME baseline as
    // the product line, which is exactly how `footerOps` already pairs them
    // (product left, page number right).
    //
    // Confirmed on a delivered case: the March 2026 report prints
    // "Page 03 of 23" on every one of its 23 pages, in Mulish, on the product
    // line. Unpadded total, matching the orthognathic report, hence no
    // `padTotal` here.
    showPageNumbers: true,
    // 🔴 Was false, inherited from the V1.7 delivered PDF. All 12 current
    // templates carry this line; V1.9.2 puts it at 273.30 in Mulish Bold 10.8.
    showNotToScale: true,
  },


  // The case report's watermark is NOT the one in pdfChassis (that is the SSE
  // report's). Measured on the reference: ~4.4x more ink in a blank margin strip
  // and a ~9.8mm vertical repeat against the chassis's 26mm. Steps below are
  // MEASURED, not tuned by eye: on the largest blank region in the reference
  // (page 22, y 210-250mm) autocorrelation gives a 9.74mm vertical repeat and a
  // ~33.6mm horizontal one, and a single mark spans 30.8mm — which is 20pt
  // Mulish at 30 degrees, not the chassis's 13pt. At 0.14 opacity a blank margin
  // strip then carries 2.05 mean-grey units of ink against the reference's 2.06,
  // with the same 241/240 darkest pixel.
  //
  // Matching total ink alone is NOT enough: a first attempt hit the right
  // coverage with 13pt at 16x8mm and looked like grey mush, because it had the
  // density right and the tile size wrong.
  // MEASURED off this template's own baked watermark layer, not fitted.
  //
  // This was a hand-set constant — 33.6 x 9.74 mm at 0.14 — autocorrelated over
  // a blank strip of ONE delivered PDF. The horizontal period locked onto half
  // the true one, so the page carried 1.73x too many marks, at 0.14 against a
  // true 0.169. The team found it on a delivered page before we did. The
  // template says 58.08 x 16.85 at 0.169, 19.97 pt Mulish, 30 degrees.
  watermark: HOUSE_WATERMARK,

  // The V1.9.2 template DRAWS its grid. Was 'open' — images floating with no
  // box — which was true of the V1.7 document. Measured: the template's
  // `Grid Single View` layers are a rectangle outline (0.11% ink, 2 column and
  // 2 row rules) and its `Grid - 6 view` layers add the dividers (3 and 6). At
  // 0.085 mm those are hairlines, which `gridRule` below already records.
  frame: 'boxed',
  gridRule: { width: 0.085 },
  viewBadge: { size: 17, inset: 2.0 },
}

/**
 * Orbital "Images for Design Confirmation" V1.3.3.
 *
 * Measured off the delivered reference at 300 and 600 dpi by the method at the
 * top of this file. Full record, including what was NOT measured, in
 * docs/case-report/ORBITAL-REFERENCE-MEASUREMENTS.md; the fidelity decisions in
 * docs/adr/0009-orbital-report-what-matching-the-reference-means.md.
 *
 * ⚠️ That reference is IMAGE-ONLY — one flat 200 dpi raster per page, whose blur
 * is ≈0.13 mm. Text sizes below are fitted from ink-band heights using Mulish's
 * ascender (0.75 em) and descender (0.21 em), which is a fit, not a reading. They
 * are starting values for the author → render → re-measure loop, and
 * __tests__/orbitalGeometry.test.js is that loop.
 *
 * The CONTENT GRID is not fitted — it was measured identical to the orthognathic
 * report (column rule 105.16 mm exact; row rules and content box within one pixel
 * at 300 dpi) and is reused verbatim. Only the header and footer are new.
 */
export const ORBITAL_GEOMETRY = {
  id: 'orbital',
  page: { w: 210, h: 297 },
  margin: 7.7,
  contentW: 195.0,                         // reused: measured identical to orthognathic

  // DERIVED, not measured. The reference's logo ink spans 18.20 mm against the
  // orthognathic report's 16.42 mm — the same PNG, ~11% larger — so the
  // orthognathic box is scaled by that ratio. Confirm by re-measuring our render.
  logo: { top: 1.38, height: 20.07 },

  // ONE title line, not two. Page 1 reads "Preview Image" and pages 2-6 read
  // "Images for Design Confirmation", both centred on the same baseline — a
  // per-page override (`page.headerTitle`), not a second line. `titleLine1` is
  // empty for this template, so both baselines are the same value.
  // Measured ink 22.73 → 27.68 (h 4.95, with the 'g' descender of "Design").
  title: { line1Baseline: 26.60, line2Baseline: 26.60, size: 14.6 },

  // Rules here are ~0.17 mm (2 px at 300 dpi), against the orthognathic 0.55 mm.
  // Measured median grey ~188, consistent with a thin BLACK line antialiased by
  // the 200 dpi raster rather than a grey line — so no colour change.
  ruleUnderTitle: { y: 34.12, width: 0.17 },

  // Measured ink 35.93 → 40.00 and 43.31 → 47.38, both h 4.06 with descenders.
  // The template render puts row 1's baseline at 39.116 — a 0.006 mm agreement
  // with the fit, so these baselines are corroborated from two directions.
  //
  // 🔴 rightColX is 119.38, the TEMPLATE value, corrected from a fitted 132.40.
  // The old comment called 132.40 "the one value that would have been badly
  // wrong if the orthognathic geometry had been assumed". It was badly wrong
  // anyway: it measured where one delivered case's ink happened to start. A
  // second delivered case starts at 148.55, and the layer in the template sits
  // at 119.38 with `justify=left` in a fixed box — identical in the fibula
  // template. Both delivered figures are per-case hand positioning. ADR 0012.
  info: { row1Baseline: 39.11, row2Baseline: 46.49, size: 12.0, rightColX: 119.38, lineH: 5.0 },
  ruleUnderInfo: { y: 50.12, width: 0.17 },

  // ⚠️ ONE authored size, per ADR 0009 — the reference varies its own.
  // Per-page fits from the measured bands: 12.5 (p2), 14.1 (p3), 12.2 (p4),
  // 12.3 (p5), 11.8 (p6). Pages 2 and 3 are both short captions that should not
  // have been shrunk and still differ by 13%, so the spread is hand-built drift.
  // 13.0 is the midpoint; `captionFit` shrinks anything that would overrun the
  // content width, which page 4's 148 mm caption needs.
  captionTop: { baseline: 55.38, size: 13.0, fit: true },
  content: { top: 56.22, bottom: 263.99 },
  // The reference carries no bottom captions. Kept so the caption ring works if
  // an operator adds one; baseline mirrors the orthognathic offset from content.
  captionBottom: { baseline: 268.87, size: 13.0 },

  ruleFooter: { y: 270.85, width: 0.17 },
  footer: {
    // Measured 0.21 mm — effectively flush, against the orthognathic 2.97 mm.
    inset: 0.2,
    // Slot ORDER matches the corrected orthognathic order: not-to-scale,
    // copyright x2, contact, product line LAST. The orthognathic fault (product
    // line in the first slot) does not repeat here.
    // Baselines = measured ink bottom minus the string's own descender.
    notToScaleBaseline: 275.02,          // ink 271.85 → 275.91
    copyrightBaselines: [279.48, 283.73], // ink 276.80 → 280.23, 281.15 → 284.45
    contactBaseline: 288.14,             // ink 285.35 → 288.14, no descender
    productBaseline: 293.23,             // ink 290.55 → 293.98
    productSize: 10.13,
    notToScaleSize: 11.99,
    copyrightSize: 9.85,
    // ⚠️ Fits from band height put this at 10.5-11.0, well above the
    // orthognathic 8.83. The reference's contact line really is larger. Flagged
    // because it is the least certain of the footer sizes.
    contactSize: 10.5,
    showPageNumbers: true,
    showNotToScale: true,
    // "Page 01 of 06" — the total is zero-padded here, unlike the orthognathic
    // report. That padding IS from the reference and is kept; what is NOT kept
    // is the reference's Helvetica page-number stamp, which was applied by a
    // page-numbering tool over a white patch (ADR 0009 §1). We draw it in Mulish
    // as part of the footer.
    padTotal: true,
  },

  // The case report's measured watermark — same document family, same tiled
  // mark. Parameters and their derivation are in ORTHOGNATHIC_GEOMETRY above.
  // MEASURED off this template's own baked watermark layer, not fitted.
  //
  // This was a hand-set constant — 33.6 x 9.74 mm at 0.14 — autocorrelated over
  // a blank strip of ONE delivered PDF. The horizontal period locked onto half
  // the true one, so the page carried 1.73x too many marks, at 0.14 against a
  // true 0.169. The team found it on a delivered page before we did. The
  // template says 58.08 x 16.85 at 0.169, 19.97 pt Mulish, 30 degrees.
  watermark: HOUSE_WATERMARK,

  frame: 'boxed',
  gridRule: { width: 0.085 },
  viewBadge: { size: 17, inset: 2.0 },     // measured: 'A' glyph 15.3 x 14.1 mm, inset 1.84

  /**
   * Render resolution this template needs, and the tightest crop it may use.
   *
   * 🔴 Computed, and it corrects a claim made in the design document. A grid cell
   * is contentW/2 = 97.5 mm = 3.84 in. At the shared 1400 px render a 45% crop
   * leaves 630 px, which prints at **164 dpi** — below the 180 dpi floor, not
   * above it as the design doc asserted. 1600 px leaves 720 px at the same crop:
   * 188 dpi.
   *
   * So the orbital template renders larger than the default, and this pair is
   * the contract the geometry test checks rather than a number in a comment.
   */
  render: { px: 1600, minCropFrac: 0.45, minDpi: 180 },
}

export const GEOMETRIES = {
  orthognathic: ORTHOGNATHIC_GEOMETRY,
  fibula: FIBULA_GEOMETRY,
  orbital: ORBITAL_GEOMETRY,
}

/*
 * `deriveGeometry` — a geometry derived per template from the generated truth record — retired
 * on 26 Sep 2026 with that record. Every format's chrome is the master chassis (template.js).
 */

/** Right edge of the content column. */
export const contentRight = (g) => g.margin + g.contentW

/** Vertical span available to a page's content block, after its captions. */
export function contentBand(g, { hasTopCaption, hasBottomCaption, subCaptionLines = 0 }) {
  const subH = subCaptionLines * (g.captionTop.size * 0.42)
  return {
    top: (hasTopCaption ? g.content.top : g.ruleUnderInfo.y + 3) + subH,
    bottom: hasBottomCaption ? g.content.bottom : g.ruleFooter.y - 2,
  }
}
